import { useState } from 'react';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { siteInfo } from '@/mocks/siteInfo';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '关于我们' },
  ];

  const pageTitle = `关于我们 - ${siteInfo.name}`;
  const pageUrl = `${siteInfo.url}/about`;

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteInfo.name,
    url: siteInfo.url,
    description: siteInfo.description,
    foundingDate: siteInfo.foundingYear,
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: siteInfo.contact.email,
    },
    sameAs: [
      'https://weibo.com/doujin-animation',
      'https://space.bilibili.com/doujin-animation',
    ],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '同人动画创作指南是什么？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '同人动画创作指南是一个面向同人动画创作者的综合指南网站，提供从零基础到进阶的动画制作教程、软件推荐、投稿平台介绍、创作心得分享和社群动态。我们致力于帮助每一位动画爱好者将脑海中的故事变成精彩动画作品。',
        },
      },
      {
        '@type': 'Question',
        name: '网站的内容是免费的吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '是的，本网站的所有文章和教程内容完全免费开放。我们相信知识应该被自由分享，每一位同人动画爱好者都可以在这里找到所需的创作资源。',
        },
      },
      {
        '@type': 'Question',
        name: '我可以投稿文章吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '我们非常欢迎同人动画创作者投稿分享经验！如果你有优质的动画制作教程、软件使用心得或创作故事，请通过本页的联系表单与我们取得联系，编辑团队会认真审阅每一篇投稿。',
        },
      },
      {
        '@type': 'Question',
        name: '如何加入同人动画社群？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '你可以关注我们的微博和B站账号获取最新动态。同时，推荐阅读社群动态板块的文章，了解线上社群和线下展会的参与方式。我们也会定期组织线上交流活动和创作挑战。',
        },
      },
    ],
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9qb9gnnahb7382ov93g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore parse errors
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后再试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络后重试');
    }
  };

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={`了解${siteInfo.name}的品牌故事、创作理念和联系方式。${siteInfo.description}`}
        keywords={`关于我们,同人动画,${siteInfo.keywords}`}
        canonicalUrl={pageUrl}
        ogType="website"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      <div className="pt-20 bg-background-50">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb items={breadcrumbItems} />

          <section className="mb-16 md:mb-20">
            <h1 className="font-heading text-3xl md:text-5xl text-foreground-950 mb-6 text-center">
              关于我们
            </h1>
            <div className="relative w-full h-48 md:h-64 rounded-lg overflow-hidden mb-8">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20cozy%20creative%20team%20workspace%20with%20animation%20artists%20collaborating%20around%20large%20table%20storyboard%20panels%20on%20wall%20drawing%20tablets%20soft%20natural%20lighting%20friendly%20atmosphere%20cream%20and%20warm%20beige%20tones%20professional%20yet%20homely%20creative%20studio%20vibe&width=1200&height=600&seq=about-hero-01&orientation=landscape"
                alt="关于我们"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="prose prose-lg max-w-none text-foreground-700 leading-relaxed space-y-4">
              <p>
                <strong>{siteInfo.name}</strong>创立于{siteInfo.foundingYear}年，是一个由同人动画爱好者共同维护的创作指南平台。
                我们深知同人动画创作之路充满挑战——从最初的灵光一现到最终的作品呈现，每一个环节都需要耐心、技巧和热情。
              </p>
              <p>
                我们的使命是为每一位同人动画创作者——无论你是第一次打开动画软件的新手，还是已经拥有多部作品的资深创作者——提供一个全面、实用、温暖的创作指南。
                这里没有高高在上的专业术语，只有真诚的分享和实用的经验。
              </p>
              <p>
                从动画制作教程到软件推荐，从投稿平台攻略到社群动态分享，我们精心整理每一篇文章，只希望让热爱动画的你少走一些弯路，多一份创作的快乐。
              </p>
            </div>
          </section>

          <section className="mb-16 md:mb-20">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-8 text-center">
              我们的数据
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              <div className="bg-background-100 rounded-lg p-6 text-center border border-background-200/70">
                <div className="text-3xl md:text-4xl font-heading text-accent-500 mb-2">{siteInfo.stats.articles}</div>
                <div className="text-sm text-foreground-500">原创文章</div>
              </div>
              <div className="bg-background-100 rounded-lg p-6 text-center border border-background-200/70">
                <div className="text-3xl md:text-4xl font-heading text-accent-500 mb-2">{siteInfo.stats.readers}</div>
                <div className="text-sm text-foreground-500">服务读者</div>
              </div>
              <div className="bg-background-100 rounded-lg p-6 text-center border border-background-200/70">
                <div className="text-3xl md:text-4xl font-heading text-accent-500 mb-2">{siteInfo.stats.contributors}</div>
                <div className="text-sm text-foreground-500">创作者贡献</div>
              </div>
              <div className="bg-background-100 rounded-lg p-6 text-center border border-background-200/70">
                <div className="text-3xl md:text-4xl font-heading text-accent-500 mb-2">{siteInfo.stats.years}年</div>
                <div className="text-sm text-foreground-500">持续运营</div>
              </div>
            </div>
          </section>

          <section className="mb-16 md:mb-20">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-8 text-center">
              联系我们
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg flex-shrink-0">
                    <i className="ri-mail-line text-primary-500 text-lg"></i>
                  </div>
                  <div>
                    <h3 className="font-heading text-foreground-950 font-bold mb-1">电子邮箱</h3>
                    <p className="text-foreground-600 text-sm">{siteInfo.contact.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-accent-100 rounded-lg flex-shrink-0">
                    <i className="ri-weibo-line text-accent-500 text-lg"></i>
                  </div>
                  <div>
                    <h3 className="font-heading text-foreground-950 font-bold mb-1">微博</h3>
                    <p className="text-foreground-600 text-sm">{siteInfo.contact.weibo}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-secondary-100 rounded-lg flex-shrink-0">
                    <i className="ri-bilibili-line text-secondary-500 text-lg"></i>
                  </div>
                  <div>
                    <h3 className="font-heading text-foreground-950 font-bold mb-1">B站</h3>
                    <p className="text-foreground-600 text-sm">{siteInfo.contact.bilibili}</p>
                  </div>
                </div>
              </div>

              <div className="bg-background-100 rounded-lg p-6 border border-background-200/70">
                <h3 className="font-heading text-lg text-foreground-950 mb-4">发送消息</h3>
                <form onSubmit={handleSubmit} data-readdy-form className="space-y-4">
                  <div>
                    <label htmlFor="about-name" className="block text-sm font-medium text-foreground-700 mb-1">姓名</label>
                    <input
                      type="text"
                      id="about-name"
                      name="name"
                      required
                      className="w-full px-3 py-2 rounded-md border border-background-200 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-primary-300 transition-colors"
                      placeholder="你的名字"
                    />
                  </div>
                  <div>
                    <label htmlFor="about-email" className="block text-sm font-medium text-foreground-700 mb-1">邮箱</label>
                    <input
                      type="email"
                      id="about-email"
                      name="email"
                      required
                      className="w-full px-3 py-2 rounded-md border border-background-200 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-primary-300 transition-colors"
                      placeholder="your@email.com"
                    />
                  </div>
                  <div>
                    <label htmlFor="about-subject" className="block text-sm font-medium text-foreground-700 mb-1">主题</label>
                    <input
                      type="text"
                      id="about-subject"
                      name="subject"
                      required
                      className="w-full px-3 py-2 rounded-md border border-background-200 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-primary-300 transition-colors"
                      placeholder="消息主题"
                    />
                  </div>
                  <div>
                    <label htmlFor="about-message" className="block text-sm font-medium text-foreground-700 mb-1">内容</label>
                    <textarea
                      id="about-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-3 py-2 rounded-md border border-background-200 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-primary-300 transition-colors resize-none"
                      placeholder="请输入你想说的话（最多500字）"
                    ></textarea>
                  </div>
                  <input
                    type="text"
                    name="phone_alt"
                    className="spam-guard-field"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="w-full py-2.5 rounded-md bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors disabled:opacity-50 whitespace-nowrap"
                  >
                    {formStatus === 'submitting' ? '发送中...' : '发送消息'}
                  </button>

                  {formStatus === 'success' && (
                    <p className="text-sm text-secondary-600 text-center">消息发送成功！我们会尽快回复你。</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-red-500 text-center">{formError}</p>
                  )}
                </form>
              </div>
            </div>
          </section>

          <section className="pb-16 md:pb-20">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-8 text-center">
              常见问题
            </h2>
            <div className="space-y-4 max-w-2xl mx-auto">
              {[
                { q: '同人动画创作指南是什么？', a: '同人动画创作指南是一个面向同人动画创作者的综合指南网站，提供从零基础到进阶的动画制作教程、软件推荐、投稿平台介绍、创作心得分享和社群动态。我们致力于帮助每一位动画爱好者将脑海中的故事变成精彩动画作品。' },
                { q: '网站的内容是免费的吗？', a: '是的，本网站的所有文章和教程内容完全免费开放。我们相信知识应该被自由分享，每一位同人动画爱好者都可以在这里找到所需的创作资源。' },
                { q: '我可以投稿文章吗？', a: '我们非常欢迎同人动画创作者投稿分享经验！如果你有优质的动画制作教程、软件使用心得或创作故事，请通过联系表单与我们取得联系，编辑团队会认真审阅每一篇投稿。' },
                { q: '如何加入同人动画社群？', a: '你可以关注我们的微博和B站账号获取最新动态。同时，推荐阅读社群动态板块的文章，了解线上社群和线下展会的参与方式。我们也会定期组织线上交流活动和创作挑战。' },
              ].map((faq, idx) => (
                <details key={idx} className="bg-background-100 rounded-lg border border-background-200/70 group">
                  <summary className="px-5 py-4 cursor-pointer flex items-center justify-between font-medium text-foreground-900 group-open:text-primary-500 transition-colors">
                    <span className="text-sm md:text-base">{faq.q}</span>
                    <i className="ri-arrow-down-s-line text-lg transition-transform group-open:rotate-180"></i>
                  </summary>
                  <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                    {faq.a}
                  </div>
                </details>
              ))}
            </div>
          </section>
        </div>
      </div>
    </>
  );
}